import React, {useEffect, useRef, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {useTranslate} from "../../../system/useTranslate";
import Icon from "../../../system/components/icon";
import BoxHeader from "../../components/box-header";
import Span from "../../../system/components/span";
import {InputText} from "primereact/inputtext";
import {MultiSelect} from "primereact/multiselect";
import {useSelector} from "react-redux";
import Avatar from "../../../system/components/avatar";
import {Avatar as PrimeAvatar}  from "primereact/avatar";
import InputLabel from "../../../system/components/input-label";
import SelectMembers from "../../tasks/components/select-members";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {Divider} from "primereact/divider";
import {useUpdateEffect} from "primereact/hooks";
import {OverlayPanel} from "primereact/overlaypanel";
import SelectIcon from "../../tasks/components/select-icon";
import {Dropdown} from "primereact/dropdown";
import {Tooltip} from "primereact/tooltip";
import {ColorPicker} from "primereact/colorpicker";
import {InputSwitch} from "primereact/inputswitch";
import {Checkbox} from "primereact/checkbox";
import {DataView} from "primereact/dataview";
import MultiSelectMembers from "../../tasks/components/multi-select-members";
import SelectMember from "../../tasks/components/select-member";
import {Accordion, AccordionTab} from "primereact/accordion";
import Can from "../../../system/can";
import {useUserAccess} from "../../../models/user-access/use-user-access";
import {
    attachProjectRolesToMemberships,
    createProjectMemberEntries,
    createProjectMemberEntry,
    mergeProjectMemberRoleSources,
    sameWorkspaceMembership,
} from "../project-member-roles";
import ArchiveEntityControl from "../../../system/components/archive-entity-control";
import {Message} from "primereact/message";

function ProjectSettingsDialog({visible, setVisible, projectData, onSubmit, onArchive, submitting = false, error = ""}) {
    const translate = useTranslate();
    const [newProjectData, setNewProjectData] = useState();
    const [projectMemberRoles, setProjectMemberRoles] = useState([]);
    const {workspaceMembershipData} = useSelector((state) => state.app);

    useEffect(() => {
        if (!visible) return;
        setNewProjectData(projectData
            ? {}
            : {owner: workspaceMembershipData, creatorUser: workspaceMembershipData});
        const projectMemberships = mergeProjectMemberRoleSources(
            projectData?.members,
            workspaceMembershipData?.workspace?.workspace_memberships,
        );
        setProjectMemberRoles(createProjectMemberEntries(projectMemberships, projectData));
        // Workspace membership is read at dialog-open time. Realtime changes to
        // unrelated app data must not reset an in-progress form.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectData?.documentId, projectData?.id, visible]);

    const modifyProjectMembers = (data)=>{
        setNewProjectData((current) => ({...current, members: data?.members || []}));
        setProjectMemberRoles(data?.roleAssignments || []);
    }


    const submit =()=>{
        let _newProjectData = {...newProjectData};
        if (_newProjectData?.members)_newProjectData = {..._newProjectData, members:_newProjectData?.members};
        if (_newProjectData?.parentProjects)_newProjectData = {..._newProjectData, parentProjects:_newProjectData?.parentProjects.map(p=>p.id)};
        if (_newProjectData?.owner) _newProjectData = {..._newProjectData, owner: _newProjectData?.owner?.id};
        if (_newProjectData?.creatorUser)_newProjectData = {..._newProjectData, creatorUser: _newProjectData?.creatorUser?.id};
        if (_newProjectData?.team)_newProjectData = {..._newProjectData, team: _newProjectData?.team?.id};
        onSubmit?.({
            value: _newProjectData,
            payload: {
                members: projectMemberRoles,
                previousMembers: projectData?.members || [],
            },
        })
    }

    return (
        <Dialog pt={{footer:()=>({className:"p-0 border-round-bottom-2xl"}),
            content:()=>({className:"p-0 h-full"})}}
                header={<Header projectData={projectData}/>}
                contentClassName="p-4" headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                footer={<Footer setVisible={setVisible} onSubmit={submit} submitting={submitting}/>}
                className="rtl w-6 border-round-bottom-2xl" visible={visible}
                onHide={()=>{if (!submitting) setVisible(false)}}>
            <div className="flex flex-column gap-3">
                {error && <Message className="text-xs" severity="error" text={error}/>}
                <div className="flex flex-wrap gap-2 w-full">
                    <InputLabel require className="flex-1" inputType="TEXT" label={translate("project-name")}
                                value={newProjectData?.title!==undefined?newProjectData?.title:projectData?.title}
                                onChange={(e)=>{setNewProjectData({...newProjectData, title:e.target.value})}}/>

                    <InputLabel require className="flex-1" label={translate("project-due-date")}>
                        <PrimeDatePicker inputClassName="w-full" value={new Date(newProjectData?.dueDate||projectData?.dueDate).getTime()/1000} onChange={(e)=>{setNewProjectData({...newProjectData, dueDate:new Date(e.value*1000)})}}/>
                    </InputLabel>

                </div>
                <div className="flex">
                    <InputLabel className="w-6" label={translate("project-owner")}>
                        <SelectMember primeDropdown={true} selectedMember={newProjectData?.owner || projectData?.owner}
                                      onChange={(membership) => {
                                          setNewProjectData({...newProjectData, owner: membership})
                                      }} hideLabel={false}
                                      members={workspaceMembershipData?.workspace?.workspace_memberships || []}/>
                    </InputLabel>
                </div>

                {/*<InputLabel className="flex-1" inputType="TEXTAREA" label={translate("strategic-hypothesis")} value={newProjectData?.hypothesis||projectData?.hypothesis} onChange={(e)=>{setNewProjectData({...newProjectData, hypothesis:e.target.value})}}/>*/}
                <InputLabel className="flex-1" inputType="RICHTEXT" label={translate("project-description")} value={newProjectData?.description!==undefined?newProjectData?.description:projectData?.description} onChange={(e)=>{setNewProjectData({...newProjectData, description:e.target.value})}}/>

                <InputLabel>
                    <IconColorPicker value={newProjectData?.color_icon||projectData?.color_icon} onChange={(e)=>{setNewProjectData({...newProjectData, color_icon:e.value})}}/>
                </InputLabel>

                <InputLabel className="flex-1" label={translate("parent-project")}>
                    <SelectProjects projectData={projectData} onChange={(e) => {setNewProjectData({...newProjectData, parentProjects: e})}}/>
                </InputLabel>
                {/*<InputLabel label={translate("project-members")}>*/}
                {/*    <SelectMembers selectedMembers={newProjectData?.members||projectData?.members} members={workspaceMembershipData?.workspace.workspace_memberships.map(e=>e.member)} onChange={(e)=>{setNewProjectData({...newProjectData, members:e.members})}}/>*/}
                {/*</InputLabel>*/}

                <div className="flex flex-wrap gap-2">
                    <InputLabel className="flex-1" inputType="TEXT" label={translate("total-time")} value={newProjectData?.totalTime||projectData?.totalTime} onChange={(e)=>{setNewProjectData({...newProjectData, totalTime:e.target.value})}}/>
                    <InputLabel className="flex-1" inputType="TEXT" label={translate("total-budget")} value={newProjectData?.totalBudget||projectData?.totalBudget} onChange={(e)=>{setNewProjectData({...newProjectData, totalBudget:e.target.value})}}/>
                </div>

                <RolesManagement rolesConfig={newProjectData?.rolesConfig||projectData?.rolesConfig} onChange={(e)=>{setNewProjectData({...newProjectData, rolesConfig:e})}}/>

                <ReminderRhythm value={newProjectData?.reminderRhythm||projectData?.reminderRhythm} onChange={(e)=>{setNewProjectData({...newProjectData, reminderRhythm:e.value})}}/>
                <ProjectMembers
                    visible={visible}
                    projectData={projectData}
                    value={newProjectData?.members ?? projectData?.members}
                    onChange={modifyProjectMembers}
                />
                {projectData && (
                    <Can permission="archive-project" scopeRolePermissions={projectData?.rolesConfig} scopeId={projectData?.documentId}>
                        <ManageProject projectData={projectData} onArchive={onArchive}/>
                    </Can>
                )}
                <Notifications value={newProjectData?.notificationConfig||projectData?.notificationConfig} onChange={(e)=>{setNewProjectData({...newProjectData, notificationConfig:e})}}/>
            </div>
        </Dialog>
    );
}

function IconColorPicker({value, onChange}) {

    const ref = useRef();
    const [colorIcon, setColorIcon] = useState(value)
    const colors = ["red","blue","purple","green","orange"]
    const colorPickerRef = useRef();
    useUpdateEffect(()=>{
        onChange?.({value:colorIcon});
    },[colorIcon])

    return(
        <div className="flex align-items-center gap-2">
            <PrimeAvatar onClick={(e)=>{ref.current.toggle(e)}} shape="circle" size="large" className="border-2 border-dashed border-300" icon={<Icon icon={value?.icon||"mi mi:image"}/>}/>
            <OverlayPanel ref={ref}>
                <SelectIcon onChange={(e)=>{setColorIcon({...colorIcon, icon:e});ref.current.hide()}}/>
            </OverlayPanel>
            <Divider className="m-1" layout="vertical"/>
            {colors.map(c=><PrimeAvatar onClick={()=>{setColorIcon({...colorIcon, color:c})}} icon={c===value?.color&&<Icon icon="mi mi:check" className="text-white"/>} className="cursor-pointer border-2 border-white shadow-2" shape="circle" size="normal" style={{backgroundColor:c}}/>)}
            <PrimeAvatar style={{backgroundColor:colorIcon?.color}} shape="circle" onClick={(e)=>{colorPickerRef.current.toggle(e)}} className="border-2 border-dashed border-300 cursor-pointer" icon={<Icon icon="mi mi:add" className="text-400"/>}/>
            <OverlayPanel pt={{root:()=>({className:"p-0"}),content:()=>({className:"p-1"})}} ref={colorPickerRef}>
                <ColorPicker inline pt={{input:()=>({className:"border-400",style:{borderRadius:'20px', border:"dashed 2px"}})}} value={colorIcon?.color} onChange={(e) => {setColorIcon({...colorIcon, color:`#${e.value}`});}} />
            </OverlayPanel>
        </div>
    )
}

function RolesManagement({ rolesConfig, onChange }) {
    const translate = useTranslate();

    const [whoCan, setWhoCan] = useState({
        manager: rolesConfig?.manager || [],
        editor: rolesConfig?.editor || [],
        viewer: rolesConfig?.viewer || []
    });

    const permissions = [
        "modify-project-settings",
        "manage-project-members",
        "share-project",
        "archive-project",
        "delete-project",
        "create-task",
        "edit-task",
        "delete-task",
        "change-task-status",
        "assign-task",
        "transfer-task",
        "create-meeting",
        "edit-meeting",
        "delete-meeting",
        "create-note",
        "edit-note",
        "delete-note",
        "upload-file",
        "delete-file",
        "manage-automation-rules",
        "manage-custom-fields",
        "create-milestone",
        "edit-milestone",
        "delete-milestone"
    ];

    const roles = ["manager", "editor", "viewer"];

    useUpdateEffect(() => {
        onChange?.(whoCan);
    }, [whoCan]);

    const togglePermission = (role, permission) => {
        setWhoCan(prev => ({
            ...prev,
            [role]: prev[role].includes(permission)
                ? prev[role].filter(p => p !== permission)
                : [...prev[role], permission]
        }));
    };

    return (
        <div className="flex flex-column border-1 border-200 border-round-xl p-2 surface-100">
            <BoxHeader
                className="text-xs"
                title={translate("advanced-access-levels")}
                icon={<Icon className="text-primary" icon="mi mi:admin_panel_settings" />}
            />

            <div className="py-3">
                <Accordion expandIcon="pi pi-chevron-down" collapseIcon="pi pi-chevron-up">
                    {roles.map(role => (
                        <AccordionTab key={role} header={translate(role)}
                                      pt={{content:()=>({className:"p-2"}),
                                          headerIcon: () => ({ className: "text-xs" }),
                                          headerAction: () => ({className: "font-normal text-xs gap-1 pr-2"})}}>
                            <div className="flex flex-wrap">
                                {permissions.map(permission => (
                                    <div key={permission} className="flex align-items-center w-4 gap-1 p-1">
                                        <Checkbox inputId={`${role}-${permission}`} checked={whoCan[role].includes(permission)} onChange={() => togglePermission(role, permission)}/>
                                        <label htmlFor={`${role}-${permission}`} className="cursor-pointer text-2xs">{translate(permission)}</label>
                                    </div>
                                ))}
                            </div>
                        </AccordionTab>
                    ))}
                </Accordion>
            </div>
        </div>
    );
}
function SelectRole({title, options, value, onChange, help}) {


    function ItemTemplate(option) {

        return(
            <div className="flex align-items-center justify-content-between p-2 pr-2 cursor-pointer">
                <div className="flex gap-2 align-items-center border-round-xl p-1 pl-2 pr-2">
                    <div className="flex flex-column">
                        <span className="text-xs">{option?.label}</span>
                    </div>
                </div>
            </div>
        )
    }

    return(
        <div className="flex align-items-center justify-content-between px-2 text-xs">
            <div className="flex align-items-center gap-2">
                <span>{title}</span>
                <div data-pr-tooltip={help} className="item-help">
                    <Icon icon="mi mi:help" className="text-lg" />
                </div>
                <Tooltip target=".item-help" className="text-2xs rtl" />
            </div>
            <MultiSelect pt={{item:()=>({className:"p-0"}),root:()=>({className:"w-10rem p-2 text-xs"})}} panelHeaderTemplate={<></>} itemClassName="rtl" options={options} value={value} onChange={onChange} itemTemplate={ItemTemplate}/>
        </div>
    )
}

function Header({projectData}) {
    const translate = useTranslate();
    return(
        <div className="flex flex-column">
            <span className="text-lg">{translate(projectData?"project-setting":"create-project")}</span>
            <span className="text-xs text-400">{projectData?.title}</span>
        </div>
    )
}

function Footer({setVisible, onSubmit, submitting}) {
    const translate = useTranslate();
    return(
        <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
            <Button icon={<Icon icon="mi mi:save" className="text-lg"/>} className="text-xs flex gap-2 border-round-lg p-2" label={translate("save-changes")} loading={submitting} disabled={submitting} onClick={onSubmit}/>
            <Button className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" label={translate("cancel")} disabled={submitting} onClick={()=>{setVisible(false)}}/>
        </div>
    )
}

function ReminderRhythm({value, onChange}) {
    const translate = useTranslate();
    const options = [
        {
            label:translate("never"),
            value:"never",
        },
        {
            label:translate("weekly"),
            value:"weekly",
        },
        {
            label:translate("two-weeks"),
            value:"two-weeks",
        },
        {
            label:translate("monthly"),
            value:"monthly",
        },
        {
            label:translate("seasonally"),
            value:"seasonally",
        }
    ]
    return(
        <div className="flex flex-column gap-2">
            <BoxHeader title={translate("reminder-rhythm")} icon={<Icon icon="mi mi:notifications_active" className="text-primary"/>}/>
            <div className="border-round surface-50 p-2 flex justify-content-between align-items-center text-xs">
                <span>{translate("reminder-rhythm-description")}</span>
                <Dropdown className="w-6" value={value} options={options} onChange={onChange} itemTemplate={(data)=>(<div className="text-xs">{translate(data.label)}</div>)} placeholder={translate("select-rhythm-placeholder")} valueTemplate={(data, props)=>(<div className="text-xs">{data?.label??props.placeholder}</div>)} />
            </div>
        </div>
    )
}

function ProjectMembers({visible, value, projectData, onChange}) {
    const translate = useTranslate();
    const memberRef = useRef();
    const [memberEntries, setMemberEntries] = useState([]);
    const userAccess = useUserAccess();
    const workspaceMemberships = useSelector((state) => (
        state.app.workspaceMembershipData?.workspace?.workspace_memberships || []
    ));

    useEffect(() => {
        if (!visible) return;
        let cancelled = false;
        const projectMemberships = Array.isArray(projectData?.members)
            ? projectData.members
            : value;
        const memberships = mergeProjectMemberRoleSources(projectMemberships, workspaceMemberships);
        setMemberEntries(createProjectMemberEntries(memberships, projectData));

        const projectId = projectData?.documentId || projectData?.id;
        if (projectId) {
            userAccess.getProjectAccessRoles(projectId).then((roles) => {
                if (cancelled) return;
                setMemberEntries(createProjectMemberEntries(
                    attachProjectRolesToMemberships(memberships, roles),
                    projectData,
                ));
            }).catch(() => {
                // Keep roles populated with the project response when the refresh fails.
            });
        }
        return () => { cancelled = true; };
        // Reinitialize only when the dialog opens or its project changes. Local
        // role edits must not be overwritten by the parent members array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, projectData?.documentId, projectData?.id]);

    useUpdateEffect(()=>{
        onChange?.({
            members: memberEntries.map((entry) => entry.workspaceMembership),
            roleAssignments: memberEntries,
        });
    },[memberEntries])

    const options = [
        {
            label:translate("manager"),
            value:"manager"
        },
        {
            label:translate("editor"),
            value:"editor"
        },
        {
            label:translate("viewer"),
            value:"viewer"
        }
    ]

    const updateUserRoles=(memberData, role)=>{
        setMemberEntries((current) => current.map((entry) => (
            sameWorkspaceMembership(entry.workspaceMembership, memberData.workspaceMembership)
                ? {...entry, role}
                : entry
        )));
    }

    const modifyMembers = (data)=>{
        if (data?.status==="added"){
            setMemberEntries((current) => (
                current.some((entry) => sameWorkspaceMembership(entry.workspaceMembership, data.member))
                    ? current
                    : [...current, createProjectMemberEntry(data.member, projectData)]
            ));
        }
        if (data?.status==="removed"){
            setMemberEntries((current) => current.filter((entry) => (
                !sameWorkspaceMembership(entry.workspaceMembership, data.member)
            )));
        }
    }

    const ItemTemplate = (data) => {
      const membership = data?.workspaceMembership;
      return(
          <div className="p-1 w-full">
              <div className="border-round flex justify-content-between">
                  <div className="flex align-items-center gap-1 text-xs">
                      <Avatar memberData={membership?.member}/>
                      <div className="flex flex-column gap-1">
                          <span>{membership?.member?.fullname}</span>
                      </div>
                  </div>
                  <div className="flex align-items-center gap-1">
                      <Dropdown pt={{input:()=>({className:"text-xs"}),item:()=>({className:"text-xs"})}} className="w-10rem" value={data?.role || "viewer"} options={options} onChange={(e)=>{updateUserRoles(data, e.value)}}/>
                      <Button
                          type="button"
                          aria-label={translate("remove-member")}
                          className="p-button-text p-button-danger p-1"
                          icon={<Icon icon="mi mi:delete"/>}
                          onClick={() => setMemberEntries((current) => current.filter((entry) => (
                              !sameWorkspaceMembership(entry.workspaceMembership, membership)
                          )))}
                      />
                  </div>
              </div>
          </div>
      )
    }
    return(
        <div className="flex flex-column gap-2">
            <BoxHeader left={<Button onClick={(e)=>{memberRef.current.toggle(e)}} className="p-button-text text-xs p-1 px-2" icon={<Icon icon="mi mi:add" className="text-lg"/>} label={translate("add-new-member")}/>} title={translate("project-members")} icon={<Icon icon="mi mi:group" className="text-primary"/>}/>
            <OverlayPanel pt={{root:()=>({className:"p-0 rtl"}),content:()=>({className:"p-0"})}} ref={memberRef}>
                <MultiSelectMembers
                    onChange={modifyMembers}
                    value={memberEntries.map((entry) => entry.workspaceMembership)}
                />
            </OverlayPanel>
            <div className="border-round surface-50 p-2">
                <DataView value={memberEntries} layout="list" itemTemplate={ItemTemplate}/>

            </div>
        </div>
    )
}

function ManageProject({projectData, onArchive}) {
    const translate = useTranslate();
    return (
        <ArchiveEntityControl
            entityName={projectData?.title}
            sectionTitle={translate("manage-project")}
            title={translate("archive-project-title")}
            description={translate("archive-project-description")}
            buttonLabel={translate("archive-project")}
            dialogTitle={translate("archive-project-confirm-title")}
            onArchive={onArchive}
        />
    );
}

function SelectProjects({projectData, onChange}) {
    const {config, workspaceMembershipData} = useSelector((state) => state.app);
    const translate = useTranslate();
    const [newParentProjectsData, setNewParentProjectsData] = useState();


    useEffect(() => {
        setNewParentProjectsData(projectData?.parentProjects);
    }, []);


    useUpdateEffect(()=>{
        if (newParentProjectsData){
            onChange?.(newParentProjectsData);
        }
    },[newParentProjectsData])


    //
    // const modifyMembers = (data)=>{
    //     if (data?.status==="added"){
    //         setNewMembersData([...newMembersData??[], data.member])
    //     }
    //     if (data?.status==="removed"){
    //         setNewMembersData([...newMembersData??[]].filter(m=>m.id!==data.member.id))
    //     }
    // }
    const ItemTemplate = (data)=>{
        return data?(
            <div className="w-fit flex align-items-center gap-1 text-xs">
                {data?.title}
            </div>
        ):(
            <div className="p-2">{translate("select-projects-placeholder")}</div>
        )
    }
    const SelectedItemTemplate = (data)=>{
        return (
            <div className="w-fit flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl">
                {/*<Avatar  memberData={data?.member}/>*/}
                {data?.title}
            </div>
        )
    }

    return(
        <div onClick={(e)=>{}} className="border-round border-1 border-300 flex justify-content-between pr-2">
            <MultiSelect selectedItemTemplate={SelectedItemTemplate}  filterTemplate={(options)=>(<InputText placeholder={translate("search-projects-placeholder")} onChange={options.filterOptions.filter} className="p-2 w-full text-xs"/>)} filter filterBy="title" placeholder={translate("select-projects-placeholder")} pt={{root:()=>({className:"w-full border-none shadow-none"}),label:()=>({className:"flex gap-1"}) ,labelContainer:()=>({className:"align-items-center flex pr-2"}) ,trigger:()=>({className:"p-3"}) , filterInput:()=>({className:"p-0"}), header:()=>({className:"p-2 flex-row-reverse"}), item:()=>({className:"flex-row-reverse justify-content-between p-2"})}} panelClassName="rtl" onChange={(e)=>{setNewParentProjectsData(e.value)}}  itemTemplate={ItemTemplate} options={workspaceMembershipData?.workspace?.projects} value={newParentProjectsData||[]}/>
        </div>
        // <div className="flex flex-column gap-2" >
        //     <OverlayPanel pt={{root:()=>({className:"p-0 rtl"}),content:()=>({className:"p-0"})}} ref={memberRef}>
        //         <MultiSelectMembers onChange={(e)=>{modifyMembers(e)}} options={workspaceMembershipData?.workspace?.workspace_memberships} value={newMembersData||[]}/>
        //     </OverlayPanel>
        //     <div className="border-round border-1 border-300 p-2 flex justify-content-between">
        //         <AvatarGroup className="ltr">
        //             {newMembersData?.map(m=>(<Avatar memberData={m.member}/>))}
        //         </AvatarGroup>
        //         <Icon icon="pi pi-chevron-down text-sm p-2" className="p-2" onClick={(e)=>{memberRef.current.toggle(e)}} />
        //     </div>
        // </div>
    )
}


function Notifications({value, onChange}) {
    const translate = useTranslate();
    const config = {
        enabled:false,
        rules:[
            {label:translate("notify-tasks-updated"), value:"notify-tasks-updated"},
            {label:translate("notify-deadlines"), value:"notify-deadlines"},
            {label:translate("notify-necessary-events"), value:"notify-necessary-events"}
        ]
    }

    return(
        <div className="flex flex-column gap-2">
            <BoxHeader title={translate("notifications")} icon={<Icon icon="mi mi:notifications" className="text-primary"/>}/>
            <BoxHeader left={<InputSwitch checked={value?.enabled} onChange={(e)=>{onChange?.({enabled:e.value, rules:[...value?.rules??[]]})}}/>} titleClassName="text-xs font-bold" title={translate("activate-notifications")} description={translate("activate-notifications-description")} descriptionClassName="text-xs text-400" className="pr-2"/>
            <div className={`surface-50 border-round p-3 flex align-items-center gap-3`}>
                {config.rules.map(config=><div className={`flex gap-1 align-items-center text-xs  ${!value?.enabled&&"p-disabled"}`}><Checkbox disabled={!value?.enabled} value={config.value} onChange={(e)=>{onChange?.(e.checked?{...value, rules:[...value?.rules??[], e.value]}:{...value, rules:value?.rules?.filter(v=>v!==e.value)})}} checked={value?.rules.includes(config.value)}/><label>{config.label}</label></div>)}
            </div>
        </div>
    )
}



export default ProjectSettingsDialog;
